import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import OnboardingLayout, { StepCard, StepNav } from "@/pages/onboarding/OnboardingLayout";
import PlanStandardStep from "@/pages/onboarding/steps/PlanStandardStep";
import TierStep from "@/pages/onboarding/steps/TierStep";
import BrokerStep from "@/pages/onboarding/steps/BrokerStep";
import BillingStep from "@/pages/onboarding/steps/BillingStep";
import {
  JUDGMENT_FIRST_SUBSTEPS,
  JUDGMENT_FIRST_VALIDATORS,
  JUDGMENT_FIRST_HARD_BLOCK,
  isJudgmentFirstReady,
  JudgmentFirstForm,
  JudgmentFirstPills,
} from "@/components/debtors/JudgmentFirstForm";
import { JudgmentInsightPanel } from "@/components/debtors/JudgmentInsightPanel";
import { centsToUSD } from "@/lib/dates";
import { useOnboarding } from "@/context/OnboardingContext";
import { useAuth } from "@/context/AuthContext";
import { apiClient, formatApiError, readAuth, writeAuth } from "@/lib/apiClient";

const TOTAL_STEPS = 6;
const STEP_KEYS = ["plan", "tier", "debtor", "insight", "brokers", "billing"];

// Validators per step
const validators = {
  plan: (d) => !!(d.plan?.path && d.plan?.debtor_count),
  tier: (d) => {
    if ((d.plan?.debtor_count || 1) === 1) return !!d.tier;
    return true;
  },
  debtor: (d) => isJudgmentFirstReady(d.debtor || {}),
  insight: () => true, // read-only step
  brokers: () => true,
  billing: (d) => {
    const b = d.billing || {};
    const cn = (b.card_number || "").replace(/\s/g, "");
    return !!(
      (b.cardholder || "").trim() &&
      /^\d{15,16}$/.test(cn) &&
      /^\d{2}\/\d{2}$/.test(b.expiry || "") &&
      /^\d{3,4}$/.test(b.cvc || "") &&
      /^\d{5}$/.test(b.zip || "")
    );
  },
};

const StandardWizard = () => {
  const nav = useNavigate();
  const { data, setSection, patchSection, savePartial, step, setStep } = useOnboarding();
  const { user } = useAuth();
  const [subIndex, setSubIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const idx = Math.max(0, STEP_KEYS.indexOf(step));
  const currentKey = STEP_KEYS[idx];
  const canContinue = (validators[currentKey] || (() => true))(data);

  const advance = useCallback(
    async (delta) => {
      const nextIdx = Math.max(0, Math.min(STEP_KEYS.length - 1, idx + delta));
      const nextKey = STEP_KEYS[nextIdx];
      setStep(nextKey);
      setSubIndex(0);
      await savePartial(nextKey);
    },
    [idx, setStep, savePartial]
  );

  const finish = useCallback(async () => {
    setSubmitting(true);
    try {
      const { data: res } = await apiClient.post("/onboarding/complete", { data });
      const auth = readAuth();
      if (auth && res.user) writeAuth({ ...auth, user: res.user });
      toast.success("Welcome to Judgment Enforcement AI");
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }, [data]);

  // Judgment sub-stepping
  const currentSubstep = JUDGMENT_FIRST_SUBSTEPS[subIndex];
  const substepValid = currentSubstep
    ? (JUDGMENT_FIRST_VALIDATORS[currentSubstep.key] || (() => true))(data.debtor || {})
    : true;
  const substepHardBlocked = JUDGMENT_FIRST_HARD_BLOCK(data.debtor || {});

  const onDebtorContinue = async () => {
    if (subIndex < JUDGMENT_FIRST_SUBSTEPS.length - 1) {
      setSubIndex((s) => s + 1);
      await savePartial(currentKey);
      return;
    }
    await advance(1);
  };
  const onDebtorBack = () => {
    if (subIndex > 0) {
      setSubIndex((s) => s - 1);
      return;
    }
    advance(-1);
  };
  const onSkipOptional = async () => {
    // Optional substep — user chose "Skip for now"
    await advance(1);
  };

  const stepNumberDisplay = idx + 1;
  const debtorDraft = data.debtor || {};
  const judgmentDraft = debtorDraft.judgment || {};

  return (
    <OnboardingLayout step={stepNumberDisplay} total={TOTAL_STEPS}>
      {currentKey === "plan" && (
        <StepCard width={780} testid="onboarding-card-plan">
          <PlanStandardStep data={data} patchSection={patchSection} />
          <StepNav
            onBack={() => advance(-1)}
            onContinue={() => advance(1)}
            disableBack
            disableContinue={!canContinue}
          />
        </StepCard>
      )}

      {currentKey === "tier" && (
        <StepCard width={780} testid="onboarding-card-tier">
          <TierStep
            data={data}
            patchSection={(_, v) => setSection("tier", v)}
            accountType="standard"
            debtorCount={data.plan?.debtor_count || 1}
          />
          <StepNav onBack={() => advance(-1)} onContinue={() => advance(1)} disableContinue={!canContinue} />
        </StepCard>
      )}

      {currentKey === "debtor" && (
        <StepCard width={720} testid="onboarding-card-debtor">
          <div className="space-y-2 mb-4">
            <h2 className="text-white font-bold text-[22px]">Set Up Your First Judgment</h2>
            <p className="text-text-secondary text-[14px]">
              Judgment-first. We only need enough to start tracking. Everything
              else can be added later from the judgment profile.
            </p>
          </div>
          <JudgmentFirstPills index={subIndex} onSelect={(i) => setSubIndex(i)} />
          <JudgmentFirstForm
            debtor={debtorDraft}
            patch={(partial) => patchSection("debtor", partial)}
            subIndex={subIndex}
            onSkipOptional={onSkipOptional}
          />
          <StepNav
            onBack={onDebtorBack}
            onContinue={onDebtorContinue}
            disableContinue={
              substepHardBlocked ||
              (subIndex === JUDGMENT_FIRST_SUBSTEPS.length - 1 && !canContinue) ||
              (subIndex < JUDGMENT_FIRST_SUBSTEPS.length - 1 && !substepValid)
            }
            continueLabel={
              subIndex === JUDGMENT_FIRST_SUBSTEPS.length - 1 ? "See Insights" : "Continue"
            }
          />
        </StepCard>
      )}

      {currentKey === "insight" && (
        <StepCard width={960} testid="onboarding-card-insight">
          <div className="space-y-2 mb-6">
            <h2 className="text-white font-bold text-[22px]">State-Specific Insights</h2>
            <p className="text-text-secondary text-[14px]">
              Here's what your judgment means in the state it was entered.
              You can edit any of this later from the judgment profile.
            </p>
          </div>
          <JudgmentInsightPanel
            payload={{
              state: judgmentDraft.state,
              date_entered: judgmentDraft.date_entered,
              court_level_variant: judgmentDraft.court_level_variant,
              lien_filed: judgmentDraft.lien_filed,
              lien_filed_at: judgmentDraft.lien_filed_at,
              known_real_property_states: judgmentDraft.known_real_property_states,
            }}
            debtorName={debtorDraft.full_name}
            judgmentAmountLabel={
              judgmentDraft.amount_cents ? centsToUSD(judgmentDraft.amount_cents) : undefined
            }
          />
          <StepNav
            onBack={() => advance(-1)}
            onContinue={() => advance(1)}
            continueLabel="Continue to Broker Preferences"
          />
        </StepCard>
      )}

      {currentKey === "brokers" && (
        <StepCard width={720} testid="onboarding-card-brokers">
          <BrokerStep data={data} setSection={setSection} accountType="standard" />
          <StepNav onBack={() => advance(-1)} onContinue={() => advance(1)} />
        </StepCard>
      )}

      {currentKey === "billing" && (
        <StepCard width={560} testid="onboarding-card-billing">
          <BillingStep data={data} patchSection={patchSection} accountType="standard" />
          <StepNav
            onBack={() => advance(-1)}
            onContinue={finish}
            disableContinue={!canContinue}
            loading={submitting}
            continueLabel="Complete Setup"
          />
        </StepCard>
      )}
    </OnboardingLayout>
  );
};

export default StandardWizard;
